type LogLevel = "debug" | "info" | "warn" | "error";

/** Same prod gate as debug-capture: staging/dev log, production does not. */
export function isDebugLogEnabled(): boolean {
  return process.env.NEXT_PUBLIC_NODE_ENV !== "PROD";
}

function shouldEmit(level: LogLevel): boolean {
  if (level === "warn" || level === "error") {
    return true;
  }
  return isDebugLogEnabled();
}

function emit(level: LogLevel, scope: string, args: unknown[]): void {
  if (!shouldEmit(level) || typeof console === "undefined") {
    return;
  }

  const write = console[level] ?? console.log;
  write.call(console, `[${scope}]`, ...args);
}

/**
 * Namespaced console logger for feature code.
 *
 * `debug`/`info` are no-ops in production. Filter DevTools by `[scope]`
 * (e.g. `[http]`). HTTP lines use `debug`, so enable Verbose to see them.
 *
 * @example
 * const log = createLogger("dining-orders");
 * log.debug("inbound", eventName, payload);
 */
export function createLogger(scope: string) {
  return {
    debug: (...args: unknown[]) => emit("debug", scope, args),
    info: (...args: unknown[]) => emit("info", scope, args),
    warn: (...args: unknown[]) => emit("warn", scope, args),
    error: (...args: unknown[]) => emit("error", scope, args),
  };
}

export const httpLog = createLogger("http");
